import { Transform } from 'engine/Transform';
import { V2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { asTuple } from 'game/lib/asTuple';
import { fromEntries } from 'game/lib/fromEntries';
import { emptyBotState } from 'game/state/BotState';
import noop from 'lodash/noop';
import { PlantEntity } from '../PlantEntity';
import { PondEntity } from '../PondEntity';
import { WaterSource } from '../WaterSource';
import { FenceFrontLeftEntity, FenceLeftEntity, FenceRightEntity } from '../fence/FenceEntity';
import { LabEntity, LabEntrance, LabRailFgEntity, LabStatue } from '../lab/LabEntity';
import { LabWallDoorEntity } from '../lab/LabWallDoorEntity';
import { LabWallEntity } from '../lab/LabWallEntity';
import { LabWallPanelEntity } from '../lab/LabWallPanelEntity';
import { ScannerEntity } from '../lab/ScannerEntity';
import { PumpEntity, StoreBackShadowEntity, StoreEntity, StoreGroundEntity, TiresEntity } from '../store/StoreEntity';
import { TentEntity } from '../tent/TentEntity';
import { RoadEntity } from './RoadEntity';
import { RockWallEntity } from './RockWallEntity';

interface OverworldProps {
  onEnterTent: () => void,
  onEnterLab: () => void,
}

const waterSources: V2[] = [
  [3400, 3490],
  [3980, 3490],
  [3400, 4010],
  [3740, 4270],
  [4750, 4270],
  [4750, 3740],
  [4750, 4000],
  [4240, 4280],
  [4240, 3690],
];

export const createOverworldEntities = ({ onEnterTent, onEnterLab }: OverworldProps) => {
  const door = {
    entity: create(LabWallDoorEntity, { state: emptyBotState }),
    config: { transform: new Transform({ position: [8070, 4310] }) },
  };

  return ({
    tent: {
      entity: create(TentEntity, { onEnter: onEnterTent }),
      config: { transform: new Transform({ position: [1936, 1250] }) },
    },
    storeBackShadow: {
      entity: create(StoreBackShadowEntity, {}),
      config: { transform: new Transform({ position: [2375, 3810] }) },
    },
    storeGround: {
      entity: create(StoreGroundEntity, {}),
      config: { transform: new Transform({ position: [690, 3400] }) },
    },
    store: {
      entity: create(StoreEntity, {}),
      config: { transform: new Transform({ position: [1780, 3841] }) },
    },
    tires1: {
      entity: create(TiresEntity, {}),
      config: { transform: new Transform({ position: [2785, 4005] }) },
    },
    plant1: {
      entity: create(PlantEntity, { waterAmount: 0, yOffset: -42 }),
      config: { transform: new Transform({ position: [2785, 4005] }) },
    },
    tires2: {
      entity: create(TiresEntity, {}),
      config: { transform: new Transform({ position: [2785, 4200] }) },
    },
    plant2: {
      entity: create(PlantEntity, { waterAmount: 0, yOffset: -42 }),
      config: { transform: new Transform({ position: [2785, 4200] }) },
    },
    tires3: {
      entity: create(TiresEntity, {}),
      config: { transform: new Transform({ position: [2785, 4395] }) },
    },
    plant3: {
      entity: create(PlantEntity, { waterAmount: 0, yOffset: -42 }),
      config: { transform: new Transform({ position: [2785, 4395] }) },
    },
    pump1: {
      entity: create(PumpEntity, {}),
      config: { transform: new Transform({ position: [1380, 4200] }) },
    },
    pump2: {
      entity: create(PumpEntity, {}),
      config: { transform: new Transform({ position: [1830, 4200] }) },
    },

    pond: {
      entity: create(PondEntity, {}),
      config: { transform: new Transform({ position: [3340, 3454] }) },
    },
    ...fromEntries(waterSources.map((position, i) => asTuple(
      `waterSource${i}`,
      {
        entity: create(WaterSource, {}),
        config: { transform: new Transform({ position }) },
      },
    ))),

    road: {
      entity: create(RoadEntity, {}),
      config: { transform: new Transform({ position: [0, 4570] }) },
    },

    fenceLeft: {
      entity: create(FenceLeftEntity, {}),
      config: { transform: new Transform({ position: [5175, 4310] }) },
    },
    fenceFrontLeft: {
      entity: create(FenceFrontLeftEntity, {}),
      config: { transform: new Transform({ position: [5175, 4315] }) },
    },
    fenceFrontRight: {
      entity: create(FenceRightEntity, {}),
      config: { transform: new Transform({ position: [8938, 4315] }) },
    },

    rockWall: {
      entity: create(RockWallEntity, {}),
      config: { transform: new Transform({ position: [4737, 0] }) },
    },
    lab: {
      entity: create(LabEntity, {}),
      config: { transform: new Transform({ position: [5900, 780] }) },
    },
    labRailFg: {
      entity: create(LabRailFgEntity, {}),
      config: { transform: new Transform({ position: [7430, 3050 + 180] }) },
    },
    labStatue: {
      entity: create(LabStatue, {}),
      config: { transform: new Transform({ position: [8060, 2830] }) },
    },
    labEntrance: {
      entity: create(LabEntrance, { onEnter: onEnterLab }),
      config: { transform: new Transform({ position: [8181, 2351] }) },
    },
    wall: {
      entity: create(LabWallEntity, {}),
      config: { transform: new Transform({ position: [7430, 4320] }) },
    },
    scanner: {
      entity: create(ScannerEntity, { door: door.entity }),
      config: { transform: new Transform({ position: [8035, 4320] }) },
    },
    door,
    panel: {
      entity: create(LabWallPanelEntity, { onHack: noop }),
      config: { transform: new Transform({ position: [8635, 4320] }) },
    },
  });
};
